import React from 'react';
import {
  default as MuiListSubheader,
  type ListSubheaderTypeMap as MuiListSubheaderTypeMap,
  type ListSubheaderProps as MuiListSubheaderProps,
} from '@mui/material/ListSubheader';
import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';
import { DistributiveOmit } from '@mui/types';

/**
 * Props that we do not want to include with ListSubheader.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type ListSubheaderBlockedProps = keyof Pick<MuiListSubheaderProps, 'color'>;

export interface ListSubheaderTypeMap<
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {},
  RootComponent extends React.ElementType = MuiListSubheaderTypeMap['defaultComponent']
> {
  props: AdditionalProps &
    DistributiveOmit<MuiListSubheaderProps, ListSubheaderBlockedProps>;
  defaultComponent: RootComponent;
}

export type ListSubheaderProps<
  RootComponent extends React.ElementType = ListSubheaderTypeMap['defaultComponent'],
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {}
> = OverrideProps<
  ListSubheaderTypeMap<AdditionalProps, RootComponent>,
  RootComponent
> & {
  component?: React.ElementType;
};

/**
 * ListSubheader displays a section heading within a List.
 *
 * Demos:
 *
 * - [List](https://unify.workiva.org/components/list?tab=code)
 *
 * API:
 *
 * - [ListSubheader API](https://unify.workiva.org/components/list?tab=props#listsubheader)
 */
export const ListSubheader: OverridableComponent<ListSubheaderTypeMap> =
  MuiListSubheader;
